import { ref, watch, type Ref } from 'vue';
import type { WorldPosition } from '@/api/dx-backend/api';
import { REST_TAGS, FIGHT_TAGS, DUEL_TAGS, type EnvTag } from '@/assets/images/backgrounds/manifest';

/**
 * Picks the backdrop for a panel from where the character actually stands.
 *
 * Three banks used to draw at random, so a character resting inside a city pub
 * could get a forest campfire. A position now says what it is through its
 * `labels`, which is already carried on the wire and already editable in the
 * admin, and the bank is filtered to plates that match.
 *
 * The order is narrowest first, exactly as `TransitionImageResolver` does it on
 * the backend: an explicit pin, then an environment match, then the whole bank.
 * Every step falls through rather than failing, so a mislabelled position gets a
 * plausible plate instead of nothing, and an empty bank gets '' — which is the
 * behaviour every caller already handles by keeping its own painted gradient.
 */

export type BackgroundBank = 'rest' | 'fight' | 'duel';

type PositionSource = Ref<WorldPosition | null> | (() => WorldPosition | null);

/* Lazy globs, never eager. With `eager: true` the hero folder once bundled
   ~9.7 MB of image to display one of them; here only the chosen file is
   fetched, at the cost of one dynamic import. */
const LOADERS: Record<BackgroundBank, Record<string, () => Promise<string>>> = {
  rest: import.meta.glob<string>('@/assets/images/backgrounds/rest/*.{png,jpg,jpeg,webp}', {
    import: 'default',
  }),
  fight: import.meta.glob<string>('@/assets/images/backgrounds/fight/*.{png,jpg,jpeg,webp}', {
    import: 'default',
  }),
  duel: import.meta.glob<string>('@/assets/images/backgrounds/duel/*.{png,jpg,jpeg,webp}', {
    import: 'default',
  }),
};

const TAGS: Record<BackgroundBank, Record<string, EnvTag[]>> = {
  rest: REST_TAGS,
  fight: FIGHT_TAGS,
  duel: DUEL_TAGS,
};

/** `/src/assets/images/backgrounds/rest/16-lighthouse-room.webp` -> `16-lighthouse-room`. */
export function slugFromPath(path: string): string {
  const file = path.split('/').pop() ?? path;
  const dot = file.lastIndexOf('.');
  return dot === -1 ? file : file.slice(0, dot);
}

/**
 * FNV-1a, 32 bit. Any stable hash would do; the point is that the same position
 * always lands on the same plate. A place that looks different every time the
 * panel opens is as unbelievable as the wrong biome — variety comes from there
 * being many positions, not from reshuffling one.
 */
export function hashKey(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function readLabels(position: WorldPosition | null | undefined): string[] {
  const labels = position?.labels;
  if (!Array.isArray(labels)) return [];
  return labels.filter((label): label is string => typeof label === 'string');
}

/**
 * The whole resolution, over an explicit list of installed slugs so it can be
 * exercised without touching the real banks.
 *
 * Returns the chosen slug, or null when nothing is installed at all.
 */
export function resolveBackgroundSlug(
  bank: BackgroundBank,
  position: WorldPosition | null | undefined,
  installedSlugs: string[],
  tags: Record<string, EnvTag[]> = TAGS[bank],
): string | null {
  const candidates = [...installedSlugs].sort();
  if (candidates.length === 0) return null;

  const labels = readLabels(position);

  /* 1. An explicit pin wins, but only if the file it names is actually here. */
  const pinPrefix = `${bank}-bg:`;
  for (const label of labels) {
    if (!label.startsWith(pinPrefix)) continue;
    const pinned = label.slice(pinPrefix.length);
    if (candidates.includes(pinned)) return pinned;
  }

  /* 2. An environment match narrows the bank. An unknown tag, or a tag no plate
        carries, leaves the filter empty — fall through rather than return
        nothing. */
  const wanted = labels
    .filter((label) => label.startsWith('env:'))
    .map((label) => label.slice('env:'.length))
    .filter(Boolean);

  let pool = candidates;
  if (wanted.length > 0) {
    const matched = candidates.filter((slug) =>
      (tags[slug] ?? []).some((tag) => wanted.includes(tag)),
    );
    if (matched.length > 0) pool = matched;
  }

  /* 3./4. Deterministic pick out of whatever pool survived. Candidates are
           sorted, so glob key order cannot perturb the result. */
  const seed = `${position?.id ?? ''}:${bank}`;
  return pool[hashKey(seed) % pool.length];
}

export function useLocationBackground(
  bank: BackgroundBank,
  position: PositionSource,
): { backgroundUrl: Ref<string> } {
  const backgroundUrl = ref<string>('');

  const loaders = LOADERS[bank] ?? {};
  const bySlug = new Map<string, () => Promise<string>>();
  for (const path of Object.keys(loaders)) {
    bySlug.set(slugFromPath(path), loaders[path]);
  }
  const installed = [...bySlug.keys()];

  const source =
    typeof position === 'function' ? position : () => (position ? position.value : null);

  /* `watch` and not `onMounted`: moving to another place has to repaint the
     panel, and the position often arrives after the component does. */
  watch(
    source,
    async (current) => {
      const slug = resolveBackgroundSlug(bank, current ?? null, installed);
      if (!slug) {
        backgroundUrl.value = '';
        return;
      }
      try {
        backgroundUrl.value = await bySlug.get(slug)!();
      } catch {
        /* A missing backdrop is not worth failing the panel over. */
        backgroundUrl.value = '';
      }
    },
    { immediate: true },
  );

  return { backgroundUrl };
}
